import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import { routes } from './routes';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { useScrollToTop } from './hooks/useScrollToTop';

// Create a new component that will use the hook inside Router context
const AppContent = () => {
  useScrollToTop();
  
  return (
    <Layout>
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={route.element}
          />
        ))}
      </Routes>
    </Layout>
  );
};

// CSP directives object for better readability
const CSP_DIRECTIVES = {
  // Define default sources
  defaultSrc: ["'self'"],
  // Script sources
  scriptSrc: ["'self'", "'unsafe-inline'", "https://vercel.com", "https://*.vercel.app"],
  // Style sources
  styleSrc: ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
  // Image sources
  imgSrc: ["'self'", "data:", "https://*.vercel.app", "https://vercel.com", "https://images.unsplash.com"],
  // Font sources
  fontSrc: ["'self'", "data:", "https://fonts.gstatic.com"],
  // Connect sources for APIs, analytics, etc.
  connectSrc: ["'self'", "https://*.vercel.app", "https://vercel.com"],
  // Media sources (audio, video)
  mediaSrc: ["'self'"],
  // Object sources
  objectSrc: ["'none'"],
  // Form action destinations
  formAction: ["'self'"],
  // Frame sources
  frameSrc: ["'self'", "https://vercel.live"],
  // Base URI restriction
  baseUri: ["'self'"],
  // Restrict use of plugins 
  pluginTypes: [],
};

function App() {
  return (
    <HelmetProvider>
      <>
        <Helmet>
          {/* Content Security Policy */}
          <meta httpEquiv="Content-Security-Policy" content={`
            default-src ${CSP_DIRECTIVES.defaultSrc.join(' ')};
            script-src ${CSP_DIRECTIVES.scriptSrc.join(' ')};
            style-src ${CSP_DIRECTIVES.styleSrc.join(' ')};
            img-src ${CSP_DIRECTIVES.imgSrc.join(' ')};
            font-src ${CSP_DIRECTIVES.fontSrc.join(' ')};
            connect-src ${CSP_DIRECTIVES.connectSrc.join(' ')};
            media-src ${CSP_DIRECTIVES.mediaSrc.join(' ')};
            object-src ${CSP_DIRECTIVES.objectSrc.join(' ')};
            form-action ${CSP_DIRECTIVES.formAction.join(' ')};
            frame-src ${CSP_DIRECTIVES.frameSrc.join(' ')};
            base-uri ${CSP_DIRECTIVES.baseUri.join(' ')};
          `} />

          {/* HTTP Strict Transport Security */}
          <meta httpEquiv="Strict-Transport-Security" content="max-age=31536000; includeSubDomains; preload" />
          
          {/* Cross-Origin-Opener-Policy */}
          <meta httpEquiv="Cross-Origin-Opener-Policy" content="same-origin" />
          
          {/* Permissions Policy */}
          <meta httpEquiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=(), interest-cohort=()" />
          
          {/* Referrer Policy */}
          <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
          
          {/* X-Content-Type-Options */}
          <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        </Helmet>

        <Router>
          <AppContent />
        </Router>
        <Analytics />
        <SpeedInsights />
      </>
    </HelmetProvider>
  );
}

export default App; 